import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    notifySatisfactionSurvey,
    retrySurveyNotification,
    retrySurveyForPassenger
} from "../../api/surveys/surveyApi";

/**
 * Hook dedicado al flujo de encuestas de satisfacción.
 * Aislado del flujo de taxi para no mezclar estados.
 *
 * @param {Function} showAlert        - Callback para mostrar alertas (severity, message).
 * @param {Function} refetchTravelers - Callback opcional para refetch de viajeros.
 */
export const useSurveyWorkflow = (showAlert, refetchTravelers) => {
    const queryClient = useQueryClient();

    // ------------------------------------------------------------
    // Mutación principal: enviar encuesta a un manifiesto
    // ------------------------------------------------------------
    const notifySurveyMutation = useMutation({
        mutationFn: (manId) => notifySatisfactionSurvey(manId),

        onSuccess: (data) => {
            const sent = data?.sent ?? 0;
            const failed = data?.failed ?? 0;
            const total = data?.total ?? 0;

            if (total === 0) {
                showAlert(
                    "No hay pasajeros elegibles para enviar la encuesta en este manifiesto.",
                    "warning"
                );
            } else if (failed === 0) {
                showAlert(
                    `Encuesta enviada correctamente a ${sent} de ${total} pasajeros.`
                );
            } else if (sent === 0) {
                showAlert(
                    `No se pudo enviar la encuesta. ${failed} de ${total} envíos fallaron.`,
                    "error"
                );
            } else {
                showAlert(
                    `Encuesta enviada: ${sent} exitosos y ${failed} fallidos de ${total}.`,
                    "warning"
                );
            }

            // Refrescar datos para que el botón desaparezca si el manifiesto ya fue notificado
            queryClient.invalidateQueries({ queryKey: ["viajeros"] });
            queryClient.invalidateQueries({ queryKey: ["service-order"] });

            if (refetchTravelers) refetchTravelers();
        },

        onError: (error) => {
            showAlert(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Error al enviar las encuestas de satisfacción.",
                "error"
            );
        }
    });

    // ------------------------------------------------------------
    // Mutación secundaria: reintentar envíos fallidos
    // ------------------------------------------------------------
    const retrySurveyMutation = useMutation({
        mutationFn: ({ manifestId, perId }) => {
            // Si viene perId → reintento individual
            // Si no → reintento masivo del manifiesto
            if (perId) {
                return retrySurveyForPassenger(manifestId, perId);
            }
            return retrySurveyNotification(manifestId);
        },

        onSuccess: (data, variables) => {
            const sent = data?.retried ?? data?.sent ?? 0;
            const failed = data?.failed ?? 0;

            if (variables?.perId) {
                // Reintento individual
                showAlert(
                    sent > 0
                        ? 'Encuesta reenviada correctamente al pasajero.'
                        : 'No se pudo reenviar la encuesta al pasajero.',
                    sent > 0 ? 'success' : 'error'
                );
            } else {
                // Reintento masivo
                if (failed === 0) {
                    showAlert(`Reintento exitoso: ${sent} encuestas enviadas.`);
                } else {
                    showAlert(
                        `Reintento: ${sent} exitosos, ${failed} aún fallidos.`,
                        'warning'
                    );
                }
            }

            queryClient.invalidateQueries({ queryKey: ["viajeros"] });
            queryClient.invalidateQueries({ queryKey: ["survey-status"] });
            if (refetchTravelers) refetchTravelers();
        },

        onError: (error) => {
            showAlert(
                error?.response?.data?.error ||
                'Error al reintentar el envío de encuestas.',
                'error'
            );
        }
    });

    return {
        notifySurveyMutation,
        retrySurveyMutation,

        isSurveyBusy:
            notifySurveyMutation.isPending ||
            notifySurveyMutation.isLoading ||
            retrySurveyMutation.isPending ||
            retrySurveyMutation.isLoading
    };
};